export const ALLOWED_FORMATS = {
  // Existing shared helper aliases.
  // 7 areas, 8 uses
  long: 'Do MMMM YYYY[,] h:mma',
  // 2 areas, 5 uses
  short: 'Do MMM YYYY',
  // 3 areas, 4 uses
  shortWithDayOfWeek: 'ddd, D MMM YYYY',
  // 7 areas, 10 uses
  onlyTime: 'LT',
  // 8 areas, 28 uses
  ymd: 'YYYY-MM-DD',

  // API and storage formats.
  // 2 areas, 5 uses
  apiTime: 'HH:mm',
  // 1 area, 1 use
  apiTimeWithSeconds: 'HH:mm:ss',
  // 1 area, 1 use
  apiDateTimeIso: 'YYYY-MM-DDTHH:mm:ssZ',

  // Date-only display formats.
  // 8 areas, 8 uses
  dateDayMonthYearSlash: 'DD/MM/YYYY',
  // 1 area, 2 uses
  dateDayMonthYearSlashNoLeadingDay: 'D/MM/YYYY',
  // 1 area, 11 uses
  dateMonthDayYearSlash: 'M/D/YYYY',
  // 7 areas, 11 uses
  dateDayMonthYear: 'D MMM YYYY',
  // 2 areas, 3 uses
  dateDayMonthYearPadded: 'DD MMM YYYY',
  // 1 area, 1 use
  dateDayMonthYearShort: 'DD MMM YY',
  // 1 area, 1 use
  dateWeekdayDayMonthYear: 'ddd, D MMM YYYY',
  // 1 area, 1 use
  dateWeekdayDayMonthYearLongMonth: 'ddd, D MMMM YYYY',
  // 2 areas, 2 uses
  dateFullWeekdayDayMonthYear: 'dddd, D MMMM YYYY',
  // 1 area, 1 use
  monthShortDay: 'MMM DD',
  // 2 areas, 5 uses
  monthShort: 'MMM',
  // 1 area, 2 uses
  monthLong: 'MMMM',
  // 1 area, 2 uses
  monthYearShort: 'MMM YYYY',
  // 1 area, 1 use
  monthYearLong: 'MMMM YYYY',
  // 2 areas, 2 uses
  year: 'YYYY',
  // 2 areas, 7 uses
  localizedDate: 'L',
  // 2 areas, 3 uses
  localizedLongDate: 'LL',
  // 1 area, 2 uses
  dayMonthLong: 'DD MMMM',

  // Time-only display formats.
  // 5 areas, 6 uses
  localizedTime: 'LT',
  // 1 area, 2 uses
  time24HourMinuteNoPadding: 'H:mm',
  // 2 areas, 5 uses
  time24HourMinute: 'HH:mm',
  // 1 area, 1 use
  time24HourMinuteMeridiem: 'HH:mma',
  // 1 area, 1 use
  time12HourMinute: 'h:mm A',
  // 1 area, 2 uses
  time12HourMinuteLower: 'h:mm a',
  // 1 area, 1 use
  time12HourMinuteCompact: 'h:mma',
  // 1 area, 1 use
  time12HourMinutePadded: 'hh:mm',
  // 1 area, 1 use
  time12HourMinuteCompactPadded: 'hh:mma',
  // 1 area, 1 use
  time12HourMinuteCompactWithZone: 'hh:mma z',

  // Date-time display formats.
  // 1 area, 1 use
  dateTimeMonthDayYearLong: 'MMMM D, YYYY',
  // 1 area, 1 use
  dateTimeMonthDayYearLongWithWeekday: 'dddd, MMMM D, YYYY',
  // 1 area, 1 use
  dateTimeMonthDayYearShort: 'MM/DD/YY, h:mm A',
  // 2 areas, 2 uses
  dateTimeDayMonthYearLocalizedTime: 'D MMM YYYY, LT',
  // 1 area, 1 use
  dateTimeCompactLocalized: 'D/M/YY, LT',
  // 1 area, 1 use
  dateTimeDayMonthYear12Hour: 'D MMM YYYY, hh:mm a',
  // 3 areas, 6 uses
  dateTimeDayMonthYearZone: 'D MMM YYYY, hh:mma z',
  // 1 area, 1 use
  dateTimeDayMonthYearPaddedZone: 'DD MMM YYYY, h:mma z',
  // 10 areas, 16 uses
  dateTimeDayMonthYearDashZone: 'DD MMM YYYY,-h:mma z',
  // 1 area, 2 uses
  dateTimeDayMonthYear24Hour: 'D MMM YYYY HH:mm',
  // 1 area, 2 uses
  dateTimeLongNoOrdinal: 'D MMMM YYYY, h:mm A',
  // 1 area, 1 use
  dateTimeLongAt: 'D MMMM YYYY [at] h:mm A',
  // 2 areas, 2 uses
  dateTimeWeekdayDayMonthYearLocalizedTime: 'ddd, D MMM YYYY, LT',
  // 2 areas, 2 uses
  dateTimeWeekdayDayMonthYearZone: 'ddd, D MMM YYYY, hh:mma z',
  // 2 areas, 2 uses
  dateTimeWeekdayPaddedDayMonthYearCompact: 'ddd, DD MMM YYYY, h:mma',
  // 1 area, 1 use
  localizedShortDateTime: 'lll',
  // 1 area, 1 use
  localizedCompactDateTime: 'llll',
  // 1 area, 1 use
  localizedCompactDateTimeWithZone: 'llll z',
  // 2 areas, 2 uses
  localizedFullDateTime: 'LLLL',

  // Timezone-only display formats.
  // 1 area, 1 use
  timezoneAbbreviation: 'z',
  // 1 area, 1 use
  timezoneAbbreviationLong: 'zz',
  // 1 area, 1 use
  utcOffset: 'Z'
} as const;

export type AllowedFormatKey = keyof typeof ALLOWED_FORMATS;
export type AllowedFormat = AllowedFormatKey | string;
const ALLOWED_FORMATS_MAP = new Map<string, string>(Object.entries(ALLOWED_FORMATS));

export const isAllowedFormatKey = (format: string): format is AllowedFormatKey =>
  Object.hasOwn(ALLOWED_FORMATS, format);

export const resolveDateFormat = (format: AllowedFormat): string =>
  ALLOWED_FORMATS_MAP.get(format) ?? format;
